/**
 * Estado do Relatório que sobrevive à visita: agrupamento, ordem e filtros
 * (o "como deixei"), mais as VISÕES SALVAS com nome.
 *
 * - Último estado: `tenantStorage`, prefixado pela unidade e varrido no
 *   logout como todo filtro operacional (terminal compartilhado).
 * - Visões salvas: chave durável (`CHAVES_DURAVEIS`), separadas por usuário
 *   DENTRO do valor — ficam neste aparelho, só para quem salvou.
 *
 * A busca por texto não é lembrada: reabrir a página com a lista "sumida"
 * por causa de um nome digitado ontem é o menor espanto ao contrário.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../../context/AuthContext";
import { getItemTenant, setItemTenant } from "../../utils/tenantStorage";
import { AGRUPAMENTOS, ORDENS } from "./dominio";
import { FILTROS_PADRAO, normalizarFiltros } from "./filtros";

export const CHAVE_ESTADO = "pharuerp:relatorios-legado:estado:v1";
export const CHAVE_VISOES = "pharuerp:relatorios-legado:visoes:v1";
export const TAMANHO_NOME_VISAO = 40;
export const MAXIMO_DE_VISOES = 20;

const ESTADO_PADRAO = Object.freeze({ agrupamento: "cliente", ordem: "critico", filtros: FILTROS_PADRAO });

function lerJson(chave) {
  try {
    return JSON.parse(getItemTenant(chave) || "null");
  } catch {
    return null;
  }
}

export function normalizarEstado(bruto) {
  return {
    agrupamento: AGRUPAMENTOS.some((a) => a.id === bruto?.agrupamento) ? bruto.agrupamento : "cliente",
    ordem: ORDENS.some((o) => o.id === bruto?.ordem) ? bruto.ordem : "critico",
    filtros: normalizarFiltros(bruto?.filtros),
  };
}

/** Forma canônica do que se guarda/compara: mesma ordem de chaves, sem a busca. */
function semBusca(estado) {
  return normalizarEstado({ ...estado, filtros: { ...estado.filtros, busca: "" } });
}

function lerVisoes(usuarioId) {
  const tudo = lerJson(CHAVE_VISOES);
  const lista = tudo && typeof tudo === "object" ? tudo[usuarioId] : null;
  if (!Array.isArray(lista)) return [];
  return lista
    .filter((v) => v && typeof v.id === "string" && typeof v.nome === "string")
    .map((v) => ({ id: v.id, nome: v.nome, estado: semBusca(normalizarEstado(v.estado)) }));
}

function gravarVisoes(usuarioId, visoes) {
  const tudo = lerJson(CHAVE_VISOES);
  const base = tudo && typeof tudo === "object" && !Array.isArray(tudo) ? tudo : {};
  setItemTenant(CHAVE_VISOES, JSON.stringify({ ...base, [usuarioId]: visoes }));
}

export default function useEstadoRelatorio() {
  const { usuario } = useAuth() || {};
  const usuarioId = String(usuario?.id || "anonimo");

  const [estado, setEstado] = useState(() => {
    const salvo = lerJson(CHAVE_ESTADO);
    return salvo ? normalizarEstado(salvo) : ESTADO_PADRAO;
  });
  const [visoes, setVisoes] = useState(() => lerVisoes(usuarioId));

  useEffect(() => {
    setVisoes(lerVisoes(usuarioId));
  }, [usuarioId]);

  useEffect(() => {
    setItemTenant(CHAVE_ESTADO, JSON.stringify(semBusca(estado)));
  }, [estado]);

  const setFiltros = useCallback((proximo) => {
    setEstado((atual) => ({
      ...atual,
      filtros: typeof proximo === "function" ? proximo(atual.filtros) : proximo,
    }));
  }, []);
  const setAgrupamento = useCallback((agrupamento) => setEstado((atual) => ({ ...atual, agrupamento })), []);
  const setOrdem = useCallback((ordem) => setEstado((atual) => ({ ...atual, ordem })), []);

  /** Salva o estado atual com um nome. Mesmo nome (sem diferenciar caixa) substitui. */
  const salvarVisao = useCallback(
    (nome) => {
      const limpo = String(nome || "").replace(/\s+/g, " ").trim().slice(0, TAMANHO_NOME_VISAO);
      if (!limpo) return null;
      const visao = { id: `v-${Date.now().toString(36)}`, nome: limpo, estado: semBusca(estado) };
      setVisoes((atuais) => {
        const semHomonima = atuais.filter((v) => v.nome.toLocaleLowerCase("pt-BR") !== limpo.toLocaleLowerCase("pt-BR"));
        const proximas = [visao, ...semHomonima].slice(0, MAXIMO_DE_VISOES);
        gravarVisoes(usuarioId, proximas);
        return proximas;
      });
      return visao;
    },
    [estado, usuarioId]
  );

  const aplicarVisao = useCallback(
    (id) => {
      const visao = visoes.find((v) => v.id === id);
      if (visao) setEstado(visao.estado);
    },
    [visoes]
  );

  const excluirVisao = useCallback(
    (id) => {
      setVisoes((atuais) => {
        const proximas = atuais.filter((v) => v.id !== id);
        gravarVisoes(usuarioId, proximas);
        return proximas;
      });
    },
    [usuarioId]
  );

  /** Visão cujo estado é exatamente o atual (para marcar o chip como ativo). */
  const visaoAtivaId = useMemo(() => {
    const atual = JSON.stringify(semBusca(estado));
    return visoes.find((v) => JSON.stringify(v.estado) === atual)?.id || null;
  }, [estado, visoes]);

  return {
    ...estado,
    setFiltros,
    setAgrupamento,
    setOrdem,
    visoes,
    visaoAtivaId,
    salvarVisao,
    aplicarVisao,
    excluirVisao,
  };
}
